import { useCallback, useEffect, useRef, useState } from "react";

/** Same-origin health probes (browser → nginx → Django). */
export const SUITE_HEALTH_TARGETS = [
  { id: "people", label: "People", healthUrl: "/api/people/health/" },
  { id: "email", label: "Email", healthUrl: "/api/email/health/" },
  { id: "docs", label: "Docs", healthUrl: "/api/docs/health/" },
  { id: "video", label: "Video", healthUrl: "/api/video/health/" },
  { id: "drive", label: "Drive", healthUrl: "/api/drive/health/" },
  { id: "calendar", label: "Calendar", healthUrl: "/api/calendar/health/" },
] as const;

export type SuiteServiceId = (typeof SUITE_HEALTH_TARGETS)[number]["id"];

export type HealthCheckEntry = {
  status?: string;
  detail?: string;
  optional?: boolean;
};

export type WecoHealthJson = {
  status?: string;
  service?: string;
  checks?: Record<string, HealthCheckEntry>;
};

export type ServiceHealthTier = "operational" | "degraded" | "critical" | "unreachable";

export type FooterAggregate = "operational" | "degraded" | "disruption" | "partial_unreachable";

export function parseHealthJson(raw: unknown): WecoHealthJson | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.status !== "string") return null;
  if (o.checks !== undefined && (typeof o.checks !== "object" || o.checks === null)) return null;
  return raw as WecoHealthJson;
}

export function hasDegradedCheck(body: WecoHealthJson): boolean {
  const checks = body.checks;
  if (!checks) return false;
  return Object.values(checks).some((c) => c?.status === "degraded");
}

export type FetchHealthInput = {
  httpStatus: number;
  body: WecoHealthJson | null;
  fetchError?: boolean;
};

/** Pure classification for tests and runtime. */
export function tierFromFetchResult(input: FetchHealthInput): ServiceHealthTier {
  if (input.fetchError) {
    return "unreachable";
  }
  if (input.httpStatus === 503) {
    return "critical";
  }
  if (input.body === null) {
    return "unreachable";
  }
  const { status } = input.body;
  if (status === "unhealthy") {
    return "critical";
  }
  if (status === "ok") {
    return hasDegradedCheck(input.body) ? "degraded" : "operational";
  }
  return "unreachable";
}

export function rollupFooterAggregate(tiers: ServiceHealthTier[]): {
  aggregate: FooterAggregate;
  label: string;
} {
  if (tiers.some((t) => t === "critical")) {
    return { aggregate: "disruption", label: "Service disruption" };
  }
  if (tiers.some((t) => t === "degraded")) {
    return { aggregate: "degraded", label: "Some systems degraded" };
  }
  if (tiers.some((t) => t === "unreachable")) {
    return {
      aggregate: "partial_unreachable",
      label: "Some systems unreachable",
    };
  }
  return { aggregate: "operational", label: "All systems operational" };
}

export type SuiteServiceHealthSnapshot = {
  id: SuiteServiceId;
  label: string;
  healthUrl: string;
  tier: ServiceHealthTier;
  httpStatus: number | null;
  latencyMs: number | null;
  checkedAt: number | null;
  body: WecoHealthJson | null;
};

const DEFAULT_TIMEOUT_MS = 12_000;

async function fetchOneHealth(
  target: (typeof SUITE_HEALTH_TARGETS)[number],
  signal: AbortSignal,
  timeoutMs = DEFAULT_TIMEOUT_MS
): Promise<SuiteServiceHealthSnapshot> {
  const started = performance.now();
  const ctrl = new AbortController();
  const timer = window.setTimeout(() => ctrl.abort(), timeoutMs);
  const onParentAbort = () => ctrl.abort();
  signal.addEventListener("abort", onParentAbort, { once: true });

  let httpStatus: number | null = null;
  let body: WecoHealthJson | null = null;
  let fetchError = false;

  try {
    const res = await fetch(target.healthUrl, {
      method: "GET",
      signal: ctrl.signal,
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    httpStatus = res.status;
    const text = await res.text();
    try {
      body = parseHealthJson(JSON.parse(text) as unknown);
    } catch {
      body = null;
    }
  } catch {
    fetchError = true;
  } finally {
    window.clearTimeout(timer);
    signal.removeEventListener("abort", onParentAbort);
  }

  const latencyMs = Math.round(performance.now() - started);
  const tier = tierFromFetchResult({
    httpStatus: httpStatus ?? 0,
    body,
    fetchError,
  });

  return {
    id: target.id,
    label: target.label,
    healthUrl: target.healthUrl,
    tier,
    httpStatus,
    latencyMs: httpStatus === null && fetchError ? null : latencyMs,
    checkedAt: Date.now(),
    body,
  };
}

export async function fetchAllSuiteHealth(
  signal: AbortSignal
): Promise<SuiteServiceHealthSnapshot[]> {
  const results = await Promise.all(
    SUITE_HEALTH_TARGETS.map((t) => fetchOneHealth(t, signal))
  );
  return results;
}

export type SuiteHealthState = {
  loading: boolean;
  services: SuiteServiceHealthSnapshot[];
  lastRefreshed: number | null;
  error: string | null;
};

export function useSuiteHealth(pollMs = 60_000) {
  const [state, setState] = useState<SuiteHealthState>({
    loading: true,
    services: [],
    lastRefreshed: null,
    error: null,
  });
  const runAbortRef = useRef<AbortController | null>(null);

  const refresh = useCallback(async () => {
    runAbortRef.current?.abort();
    const ac = new AbortController();
    runAbortRef.current = ac;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const services = await fetchAllSuiteHealth(ac.signal);
      if (ac.signal.aborted) return;
      setState({
        loading: false,
        services,
        lastRefreshed: Date.now(),
        error: null,
      });
    } catch (e) {
      if (ac.signal.aborted) return;
      if (e instanceof DOMException && e.name === "AbortError") return;
      if (e instanceof Error && e.name === "AbortError") return;
      const msg = e instanceof Error ? e.message : "Health check failed";
      setState((s) => ({
        ...s,
        loading: false,
        error: msg,
      }));
    }
  }, []);

  useEffect(() => {
    void refresh();
    const id = window.setInterval(() => void refresh(), pollMs);
    return () => {
      window.clearInterval(id);
      runAbortRef.current?.abort();
    };
  }, [refresh, pollMs]);

  const tiers = state.services.map((s) => s.tier);
  const footerRollup =
    state.services.length === 0 && state.loading
      ? { aggregate: "operational" as FooterAggregate, label: "Checking system status" }
      : state.services.length > 0
        ? rollupFooterAggregate(tiers)
        : state.error
          ? {
              aggregate: "disruption" as FooterAggregate,
              label: "Unable to load system status",
            }
          : {
              aggregate: "operational" as FooterAggregate,
              label: "All systems operational",
            };

  return {
    ...state,
    aggregate: footerRollup.aggregate,
    footerLabel: footerRollup.label,
    refresh,
  };
}
